import { useState, useEffect } from "react";

import {
    normalizeDni,
    getIIEEFiltradasByProyecto,
    getAutoSelectedProyecto,
    getAutoSelectedIIEE,
    resetLoginState,
    shouldShowProyectoSelect,
    shouldShowIIEESelect,
} from "./loginHelpers";

const MAX_INTENTOS = 4;
const TIEMPO_BLOQUEO_SEG = 30;

export const useLoginForm = () => {
    const [dni, setDni] = useState("");
    const [password, setPassword] = useState("");
    const [regionesFiltradas, setRegionesFiltradas] = useState([]);
    const [proy, setProy] = useState("");

    const [iieeOptions, setIIEEOptions] = useState([]);
    const [iieeFiltradas, setIIEEFiltradas] = useState([]);
    const [iiee, setIIEE] = useState("");

    // Control de intentos
    const [intentosFallidos, setIntentosFallidos] = useState(0);
    const intentosRestantes = MAX_INTENTOS - intentosFallidos;
    const [tiempoBloqueo, setTiempoBloqueo] = useState(0);

    const proyectoActivo =
        proy || (regionesFiltradas.length === 1 ? regionesFiltradas[0]?.id ?? "" : "");

    const showProyectoSelect = shouldShowProyectoSelect(regionesFiltradas);

    const showIIEESelect =
        shouldShowIIEESelect(regionesFiltradas, iieeOptions) &&
        (!!proyectoActivo || regionesFiltradas.length === 1);

    // Efecto para manejar la cuenta regresiva del bloqueo
    useEffect(() => {
        let timer;
        if (tiempoBloqueo > 0) {
            timer = setInterval(() => {
                setTiempoBloqueo((prev) => prev - 1);
            }, 1000);
        } else if (tiempoBloqueo === 0 && intentosFallidos >= MAX_INTENTOS) {
            // Reiniciar intentos cuando termine el tiempo de bloqueo
            setIntentosFallidos(0);
        }
        return () => clearInterval(timer);
    }, [tiempoBloqueo, intentosFallidos]);

    const helperText =
        tiempoBloqueo > 0
            ? `Demasiados intentos. Espera ${tiempoBloqueo}s.`
            : dni.length < 8
                ? "Ingresa tu DNI (8 dígitos)"
                : "";

    const getProyectoLabel = (selected) => {
        if (!selected) return "Selecciona un proyecto";

        const region = regionesFiltradas.find(
            (item) => String(item.id) === String(selected)
        );

        return region?.nombre || "Selecciona un proyecto";
    };

    const resetFormState = () => {
        resetLoginState({
            setIIEEOptions,
            setIIEE,
            setRegionesFiltradas,
            setProy,
            setIIEEFiltradas,
        });
    };

    const cargarContextos = (contextos = []) => {
        const contextosOrdenados = [...(Array.isArray(contextos) ? contextos : [])].sort((a, b) => {
            const left = [
                a?.region ?? "",
                a?.nombre_ie ?? "",
                a?.nivel_modalidad ?? "",
                a?.rol_nombre ?? "",
            ].join("|");
            const right = [
                b?.region ?? "",
                b?.nombre_ie ?? "",
                b?.nivel_modalidad ?? "",
                b?.rol_nombre ?? "",
            ].join("|");
            return left.localeCompare(right, "es");
        });

        const regiones = Array.from(
            new Map(
                contextosOrdenados.map((item) => [
                    String(item?.region_id ?? ""),
                    {
                        id: String(item?.region_id ?? ""),
                        nombre: item?.region ?? "",
                    }
                ])
            ).values()
        ).filter((region) => region.id && region.nombre);

        setIIEEOptions(contextosOrdenados);
        setRegionesFiltradas(regiones);
        setProy(getAutoSelectedProyecto(regiones));
    };

    useEffect(() => {
        if (!proyectoActivo) {
            setIIEEFiltradas([]);
            setIIEE("");
            return;
        }

        const filtradas = getIIEEFiltradasByProyecto(
            proyectoActivo,
            regionesFiltradas,
            iieeOptions
        );

        setIIEEFiltradas(filtradas);
        setIIEE(getAutoSelectedIIEE(filtradas));
    }, [proyectoActivo, regionesFiltradas, iieeOptions]);

    // NUEVA FUNCIÓN: Suma un error cuando falla el login al hacer submit
    const registrarIntentoFallido = () => {
        const nuevosIntentos = intentosFallidos + 1;
        setIntentosFallidos(nuevosIntentos);
        if (nuevosIntentos >= MAX_INTENTOS) {
            setTiempoBloqueo(TIEMPO_BLOQUEO_SEG);
        }
    };

    const handleDniChange = (e) => {
        // Evitar procesar si está bloqueado por demasiados intentos
        if (tiempoBloqueo > 0) return;

        const value = normalizeDni(e.target.value);
        setDni(value);

        if (value.length < 8) {
            resetFormState();
        }
    };

    const handlePasswordChange = (e) => {
        if (tiempoBloqueo > 0) return;
        setPassword(e.target.value);
    };

    const resetContextSelection = () => {
        resetFormState();
    };

    return {
        dni,
        password,
        proy,
        iiee,
        iieeFiltradas,
        regionesFiltradas,
        iieeOptions,
        proyectoActivo,
        showProyectoSelect,
        showIIEESelect,
        helperText,
        handleDniChange,
        handlePasswordChange,
        cargarContextos,
        resetContextSelection,
        setProy,
        setIIEE,
        getProyectoLabel,
        tiempoBloqueo,
        registrarIntentoFallido,
        intentosRestantes,
    };
};
